// entityForm.ts — the KYB business-entity payload shared between every place that creates one:
// the onboarding wizard's first entity (containers/onboarding.tsx) and the dashboard's "Add entity"
// page (containers/dashboard-add-entity.tsx) for every entity after that. Kept in one place so the
// two forms can never drift on what `POST /v2/entities` actually needs.

import { PAYOUTS_TERMS_VERSION } from '@/config/terms';

export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// ---- field validation ------------------------------------------------------------------------
// Client-side mirrors of what payouts enforces on `POST /v2/entities` (CreateEntityRequest in
// payouts.api.external.yaml, plus the request validators in partnerships-api), so a bad value is
// caught on the step it was typed on instead of surfacing as a 400 after the whole wizard is submitted.

/** One validated form field: `test` defaults to "non-empty"; `message` is the inline error. */
export interface FieldSpec {
  name: string;
  label: string;
  test?: (v: any) => boolean;
  message?: string;
}

const str = (v: unknown) => String(v ?? '').trim();
const digits = (v: unknown) => String(v ?? '').replace(/\D/g, '');

/** Contact/owner names: at least 2 characters of letters, spaces, hyphens, dots or commas (the `Contact` rule). */
export const isPersonName = (v: unknown) => /^[A-Za-z][A-Za-z .,-]*$/.test(str(v)) && str(v).length >= 2;

/** Business (DBA) and legal name: 3–100 characters. */
export const isBusinessName = (v: unknown) => str(v).length >= 3 && str(v).length <= 100;

/** A US phone number: 10 digits, or 11 with the leading country code 1. */
export const isUsPhone = (v: unknown) => {
  const d = digits(v);
  return d.length === 10 || (d.length === 11 && d.startsWith('1'));
};

// Same hostname rule payouts applies (`requireWebsite`): optional http(s) scheme, a dotted hostname
// with a 2+ letter TLD, then an optional port/path/query/fragment.
const WEBSITE_RE =
  /^(https?:\/\/)?([a-z0-9]([a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}(:(6553[0-5]|655[0-2]\d|65[0-4]\d{2}|6[0-4]\d{3}|[1-5]\d{4}|[1-9]\d{0,3}|0))?(\/[^\s?#]*)?(\?[^\s#]*)?(#\S*)?$/i;
export const isWebsite = (v: unknown) => WEBSITE_RE.test(str(v));

/** A 9-digit EIN (12-3456789) or SSN (123-45-6789), dashes optional — the `TaxInfo.identifier` pattern. */
export const isTaxId = (v: unknown) => /^[0-9]{2}-?[0-9]{7}$|^[0-9]{3}-?[0-9]{2}-?[0-9]{4}$/.test(str(v));

/** Input mask for the tax id field: caps it at 9 digits and dashes it as the chosen type is written. */
export const taxIdMaskFor = (type: string): (string | RegExp)[] => {
  const d = /\d/;
  return type === 'ein' ? [d, d, '-', d, d, d, d, d, d, d] : [d, d, d, '-', d, d, '-', d, d, d, d];
};

// The USPS state and territory codes payouts accepts (`USA_STATES` in @melio/melio-db).
const US_STATE_CODES = new Set(
  (
    'AL AK AS AZ AR CA CO CT DE DC FM FL GA GU HI ID IL IN IA KS KY LA ME MH MD MA MI MN MS MO MT NE NV NH NJ NM NY NC ND MP ' +
    'OH OK OR PW PA PR RI SC SD TN TX UT VT VI VA WA WV WI WY'
  ).split(' ')
);
export const isUsState = (v: unknown) => US_STATE_CODES.has(str(v).toUpperCase());

/** A US ZIP: 5 or 9 digits, with or without a hyphen before the last 4. */
export const isZip = (v: unknown) => /^\d{5}(-?\d{4})?$/.test(str(v));

/** A real date of birth for someone between 18 and 120 years old (the `Owner` rule). */
export function isAdultDob(v: unknown, now = new Date()): boolean {
  if (!(v instanceof Date) || isNaN(v.getTime())) return false;
  let age = now.getFullYear() - v.getFullYear();
  const m = now.getMonth() - v.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < v.getDate())) age--;
  return age >= 18 && age <= 120;
}

export const ownerSpecs: FieldSpec[] = [
  { name: 'ownerFirst', label: 'First name', test: isPersonName, message: 'At least 2 letters (letters, spaces, hyphens, dots)' },
  { name: 'ownerLast', label: 'Last name', test: isPersonName, message: 'At least 2 letters (letters, spaces, hyphens, dots)' },
  // 254 characters is the most an email address can be (RFC 5321).
  { name: 'ownerEmail', label: 'Owner email', test: (v) => EMAIL_RE.test(str(v)) && str(v).length <= 254, message: 'Enter a valid email' },
  { name: 'ownerDob', label: 'Date of birth', test: isAdultDob, message: 'The owner must be between 18 and 120 years old' },
  { name: 'ownerPhone', label: 'Phone number', test: isUsPhone, message: 'Enter a 10-digit US phone number' },
];
export const businessInfoSpecs: FieldSpec[] = [
  { name: 'legalName', label: 'Business name', test: isBusinessName, message: 'Between 3 and 100 characters' },
  { name: 'industry', label: 'Industry', test: (v) => !!(v && v.naicsCode), message: 'Select an industry from the list' },
  { name: 'phone', label: 'Business phone', test: isUsPhone, message: 'Enter a 10-digit US phone number' },
  // Required: the website becomes the partner's product URL in the email-service config.
  { name: 'website', label: 'Business website', test: isWebsite, message: 'Enter a valid website (e.g. https://example.com)' },
];
export const legalInfoSpecs: FieldSpec[] = [
  { name: 'businessType', label: 'Business type', message: 'Select a business type' },
  { name: 'taxIdType', label: 'Tax ID type', message: 'Select a tax ID type' },
  { name: 'taxId', label: 'Tax ID', test: isTaxId, message: 'Enter all 9 digits' },
];
const addressSpecsFor = (parent: string): FieldSpec[] => [
  { name: `${parent}.line1`, label: 'Address line 1' },
  { name: `${parent}.city`, label: 'City' },
  { name: `${parent}.state`, label: 'State', test: isUsState, message: 'A two-letter US state code (e.g. NY)' },
  { name: `${parent}.postalCode`, label: 'Postal code', test: isZip, message: 'A 5 or 9 digit ZIP code' },
];
export const legalAddressSpecs = addressSpecsFor('legalAddress');
export const addressSpecs = addressSpecsFor('address');

/**
 * Checks `specs` against the form's current values: clears then sets each field's inline error and
 * returns the banner line for the first failure (empty when everything passes).
 */
export function validateSpecs(
  specs: FieldSpec[],
  values: Record<string, any>,
  setError: (name: string, error: { type: string; message: string }) => void,
  clearErrors: (name: string) => void
): string {
  const get = (path: string) => path.split('.').reduce<any>((o, k) => (o == null ? o : o[k]), values);
  let firstMsg = '';
  for (const s of specs) {
    clearErrors(s.name);
    const val = get(s.name);
    const filled = !(val == null || str(val) === '');
    const ok = s.test ? s.test(val) : filled;
    if (!ok) {
      // An empty field reads "Required", unless the spec is presence-only and words that itself.
      const message = filled ? s.message || 'Invalid value' : (!s.test && s.message) || 'Required';
      setError(s.name, { type: filled ? 'validate' : 'required', message });
      if (!firstMsg) firstMsg = filled ? `Please check: ${s.label}` : `Please complete: ${s.label}`;
    }
  }
  return firstMsg;
}

// The `POST /v2/entities` field paths payouts names in its 400s, mapped back to the form field that
// supplied each one. `contact.*` is the owner's name too (see buildEntityBody).
const FORM_FIELD_FOR_PATH: Record<string, string> = {
  name: 'legalName',
  legalName: 'legalName',
  phoneNumber: 'phone',
  website: 'website',
  industry: 'industry',
  'industry.naicsCode': 'industry',
  businessType: 'businessType',
  taxInfo: 'taxId',
  'taxInfo.type': 'taxIdType',
  'taxInfo.identifier': 'taxId',
  contact: 'ownerFirst',
  'contact.firstName': 'ownerFirst',
  'contact.lastName': 'ownerLast',
  owner: 'ownerFirst',
  'owner.firstName': 'ownerFirst',
  'owner.lastName': 'ownerLast',
  'owner.email': 'ownerEmail',
  'owner.dateOfBirth': 'ownerDob',
  'owner.phoneNumber': 'ownerPhone',
};
for (const parent of ['legalAddress', 'address']) {
  for (const key of ['line1', 'line2', 'city', 'state', 'postalCode', 'aptNumber'])
    FORM_FIELD_FOR_PATH[`${parent}.${key}`] = `${parent}.${key}`;
  FORM_FIELD_FOR_PATH[parent] = `${parent}.line1`;
}

/** A payouts rejection traced back to the form field it is about. */
export interface EntityFieldError {
  /** The form field name (as registered with the form, e.g. `legalAddress.postalCode`). */
  field: string;
  /** The field's on-screen label. */
  label: string;
  /** The inline hint to show on the field. */
  hint: string;
}

const ALL_ENTITY_SPECS = [...ownerSpecs, ...businessInfoSpecs, ...legalInfoSpecs, ...legalAddressSpecs, ...addressSpecs];

/**
 * Recognizes a payouts validation error about one entity field — `Missing or invalid field: owner.email`,
 * `Invalid website: website`, `Invalid businessType: ngo`, ... — and returns the form field it is about,
 * so the form can point at that field instead of showing the raw API message. `null` for anything else
 * (a conflict, a server error, a message that names no field).
 */
export function entityFieldErrorFrom(ex: unknown): EntityFieldError | null {
  const e = ex as { status?: number; message?: string } | null;
  if (!e || e.status !== 400 || typeof e.message !== 'string') return null;
  const match = /^(?:Missing or invalid field|Missing field|Invalid field|Invalid website|Invalid email|Invalid)[: ]\s*([A-Za-z.]+)/.exec(
    e.message
  );
  const field = match && FORM_FIELD_FOR_PATH[match[1]];
  if (!field) return null;
  const spec = ALL_ENTITY_SPECS.find((s) => s.name === field);
  const label = spec?.label ?? (field.endsWith('aptNumber') ? 'Apt / unit number' : field.endsWith('line2') ? 'Address line 2' : field);
  return { field, label, hint: spec?.message || 'This value was not accepted' };
}

export const emptyAddress = { _search: undefined, line1: '', line2: '', city: '', state: '', postalCode: '', aptNumber: '' };

export const businessTypeOptions = [
  {
    value: 'sole-proprietorship',
    label: 'Sole Proprietorship',
    description: 'An unincorporated one-person business (e.g., freelancer, independent contractor).',
  },
  { value: 'partnership', label: 'Partnership', description: 'A business owned by two or more people (e.g., General, LP, LLP).' },
  {
    value: 'llc',
    label: 'Limited Liability Company (LLC)',
    description: 'A registered business structure that protects owners from personal liability.',
  },
  { value: 'corporation', label: 'Corporation', description: 'A registered legal entity (e.g., C-Corp, S-Corp, Inc.).' },
  { value: 'trust', label: 'Trust', description: 'A fiduciary arrangement managed by a trustee on behalf of beneficiaries.' },
  {
    value: 'non-profit',
    label: 'Non-Profit',
    description: 'A tax-exempt organization operating for charitable, educational, or public purposes.',
  },
  { value: 'municipality', label: 'Municipality', description: 'A local government entity such as a city, town, or county.' },
  { value: 'ngo', label: 'NGO', description: 'A non-governmental organization operating independently of government.' },
];

/**
 * Allowed tax id types per formation type, mirroring `TaxInfo` / `CreateEntityRequest` in
 * payouts.api.external.yaml: `ein` is accepted for every formation type, `ssn` only for
 * sole-proprietorship and trust. `itin` is still in the schema enum for backward compatibility
 * but is no longer accepted for any formation type, so the portal never offers it.
 */
export const taxTypesForBusinessType = (type: string): string[] =>
  type === 'sole-proprietorship' || type === 'trust' ? ['ein', 'ssn'] : ['ein'];

export const taxTypeLabels: Record<string, string> = { ein: 'EIN', ssn: 'SSN', itin: 'ITIN' };

export const taxTypeOptionsFor = (type: string) => taxTypesForBusinessType(type).map((value) => ({ value, label: taxTypeLabels[value] }));

/**
 * Keeps the selected tax id type valid when the formation type changes: an already-allowed
 * choice is left alone (so picking SSN then re-picking sole-proprietorship doesn't reset it),
 * anything else falls back to `ein`, which every formation type accepts.
 */
export const coerceTaxTypeForBusinessType = (type: string, current: string) =>
  taxTypesForBusinessType(type).includes(current) ? current : 'ein';

/** Drops the throwaway `_search` autocomplete field and blank optionals before sending an address. */
export const mkAddress = (a: any) => ({
  line1: a.line1,
  ...(a.line2 ? { line2: a.line2 } : {}),
  city: a.city,
  state: a.state,
  postalCode: a.postalCode,
  ...(a.aptNumber ? { aptNumber: a.aptNumber } : {}),
  countryCode: 'US',
});

/**
 * The full set of form values a business-entity create needs — the same fields whether it's the
 * onboarding wizard's first entity or a later one added from the dashboard.
 */
export interface EntityFormValues {
  ownerFirst: string;
  ownerLast: string;
  ownerEmail: string;
  ownerPhone: string;
  ownerDob: Date | null;
  legalName: string;
  industry?: { name?: string; naicsCode?: string | null };
  phone: string;
  website: string;
  businessType: string;
  taxIdType: string;
  taxId: string;
  address: Record<string, any>;
  legalAddress: Record<string, any>;
  operatingSameAsLegal: boolean;
}

/**
 * Builds the `POST /v2/entities` body from a form's values. Mints a fresh `externalId` every call.
 * `contact` (the account's point of contact) is the business owner's name — there's no separate
 * "point of contact" field collected anywhere; the owner doubles as the contact.
 */
export function buildEntityBody(v: EntityFormValues): Record<string, unknown> {
  const legalAddr = mkAddress(v.legalAddress);
  const operatingAddr = v.operatingSameAsLegal ? legalAddr : mkAddress(v.address);
  return {
    type: 'business',
    externalId: 'partner-biz-' + crypto.randomUUID().slice(0, 8),
    name: v.legalName,
    legalName: v.legalName,
    phoneNumber: v.phone,
    businessType: v.businessType,
    taxInfo: { type: v.taxIdType, identifier: v.taxId },
    industry: { naicsCode: v.industry?.naicsCode, ...(v.industry?.name ? { name: v.industry.name } : {}) },
    contact: { firstName: v.ownerFirst, lastName: v.ownerLast },
    ...(v.website ? { website: v.website } : {}),
    address: operatingAddr,
    legalAddress: legalAddr,
    owner: {
      firstName: v.ownerFirst,
      lastName: v.ownerLast,
      email: v.ownerEmail,
      ...(v.ownerDob instanceof Date ? { dateOfBirth: v.ownerDob.toISOString().split('T')[0] } : {}),
      ...(v.ownerPhone ? { phoneNumber: v.ownerPhone } : {}),
    },
    // `acceptedAt` is the browser clock at submit (ISO-8601 with `Z`; payouts bounds it to 90 days past /
    // 14 hours future). `version` must equal the version payouts has in force or the create is rejected.
    termsAcceptance: { accepted: true, acceptedAt: new Date().toISOString(), version: PAYOUTS_TERMS_VERSION },
  };
}
